import * as Phaser from 'phaser';
import { GAME_EVENTS, SCENES, type StageChangedPayload } from '@/types';
import { GAME_HEIGHT, GAME_WIDTH } from '@/config/gameConfig';
import { BALANCE } from '@/config/balance';
import type { GameScene } from './GameScene';

const STRIP_HEIGHT = 56;
const HEART_COUNT = 5;
const HEART_RADIUS = 7;
const HEART_GAP = 18;
const PROG_BAR_HEIGHT = 6;
const PROG_BAR_MARGIN_X = 12;
const PROG_BAR_Y = STRIP_HEIGHT - PROG_BAR_HEIGHT - 4;

export class HUDScene extends Phaser.Scene {
  private gameScene?: GameScene;
  private scoreText?: Phaser.GameObjects.Text;
  private timerText?: Phaser.GameObjects.Text;
  private hearts: Phaser.GameObjects.Arc[] = [];
  private progressBg?: Phaser.GameObjects.Graphics;
  private progressFill?: Phaser.GameObjects.Graphics;
  private shieldIcon?: Phaser.GameObjects.Image;
  private shieldRemainText?: Phaser.GameObjects.Text;
  private soundEnabled = false;
  private soundBtnText?: Phaser.GameObjects.Text;
  private currentScore = 0;
  private fpsText?: Phaser.GameObjects.Text;

  constructor() {
    super(SCENES.HUD);
  }

  create(): void {
    this.gameScene = this.scene.get(SCENES.Game) as GameScene;
    // Reset stale state từ game session trước (HUDScene instance persist)
    this.hearts = [];
    this.currentScore = 0;
    this.scoreText = undefined;
    this.timerText = undefined;
    this.progressBg = undefined;
    this.progressFill = undefined;
    this.shieldIcon = undefined;
    this.shieldRemainText = undefined;
    this.soundBtnText = undefined;
    this.fpsText = undefined;

    this.drawTopStrip();
    this.drawScore();
    this.drawHearts();
    this.drawTimer();
    this.drawProgressBar();
    this.drawShieldIndicator();
    this.drawSoundToggle();
    this.drawFpsIfRequested();
    this.bindGameEvents();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unbindGameEvents());
  }

  override update(): void {
    if (this.fpsText) {
      this.fpsText.setText(`${Math.round(this.game.loop.actualFps)} fps`);
    }
    if (!this.gameScene || !this.scene.isActive(SCENES.Game)) return;
    const elapsed = this.gameScene.getElapsedMs();
    const remaining = Math.max(0, BALANCE.game.durationMs - elapsed);
    const sec = Math.ceil(remaining / 1000);
    if (this.timerText) {
      this.timerText.setText(String(sec));
      this.timerText.setColor(
        sec <= BALANCE.ui.timerWarnSecondsLeft ? '#ff4444' : '#ffffff',
      );
    }

    const shieldRemainMs = this.gameScene.getShieldRemainingMs();
    if (shieldRemainMs > 0) {
      this.shieldIcon?.setVisible(true);
      this.shieldRemainText?.setVisible(true);
      this.shieldRemainText?.setText(`${(shieldRemainMs / 1000).toFixed(1)}s`);
    } else {
      this.shieldIcon?.setVisible(false);
      this.shieldRemainText?.setVisible(false);
    }
  }

  private drawTopStrip(): void {
    const g = this.add.graphics().setDepth(0);
    g.fillStyle(0x1a1a2e, 0.55);
    g.fillRect(0, 0, GAME_WIDTH, STRIP_HEIGHT);
  }

  private drawScore(): void {
    this.scoreText = this.add
      .text(12, 8, '0', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0, 0)
      .setDepth(2)
      .setShadow(0, 2, '#0008', 3, true, true);
  }

  private drawHearts(): void {
    const totalWidth = (HEART_COUNT - 1) * HEART_GAP;
    const startX = GAME_WIDTH / 2 - totalWidth / 2;
    const y = 18;
    for (let i = 0; i < HEART_COUNT; i++) {
      const x = startX + i * HEART_GAP;
      const heart = this.add
        .arc(x, y, HEART_RADIUS, 0, 360, false, 0xffffff, 0.18)
        .setStrokeStyle(1.5, 0xffffff, 0.9)
        .setDepth(2);
      this.hearts.push(heart);
    }
    this.applyHearts(0);
  }

  private applyHearts(stage: number): void {
    this.hearts.forEach((h, i) => {
      if (i <= stage) {
        h.setFillStyle(0xffd84a, 1);
      } else {
        h.setFillStyle(0xffffff, 0.18);
      }
    });
  }

  private drawTimer(): void {
    this.timerText = this.add
      .text(GAME_WIDTH - 12, 8, '60', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setDepth(2)
      .setShadow(0, 2, '#0008', 3, true, true);
  }

  private drawProgressBar(): void {
    const w = GAME_WIDTH - PROG_BAR_MARGIN_X * 2;
    this.progressBg = this.add.graphics().setDepth(1);
    this.progressBg.fillStyle(0xffffff, 0.25);
    this.progressBg.fillRoundedRect(PROG_BAR_MARGIN_X, PROG_BAR_Y, w, PROG_BAR_HEIGHT, 3);
    this.progressFill = this.add.graphics().setDepth(2);
    this.renderProgress(0);
  }

  private renderProgress(value: number): void {
    if (!this.progressFill) return;
    const w = GAME_WIDTH - PROG_BAR_MARGIN_X * 2;
    const filled = (Math.min(100, Math.max(0, value)) / 100) * w;
    this.progressFill.clear();
    this.progressFill.fillStyle(0x3b9f4a, 1);
    this.progressFill.fillRoundedRect(PROG_BAR_MARGIN_X, PROG_BAR_Y, filled, PROG_BAR_HEIGHT, 3);
  }

  private drawShieldIndicator(): void {
    this.shieldIcon = this.add
      .image(GAME_WIDTH - 18, STRIP_HEIGHT + 14, 'tex_shield')
      .setScale(0.45)
      .setOrigin(0.5)
      .setDepth(3)
      .setVisible(false);
    this.shieldRemainText = this.add
      .text(GAME_WIDTH - 40, STRIP_HEIGHT + 8, '0.0s', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#ffd84a',
        stroke: '#1a1a1a',
        strokeThickness: 3,
      })
      .setOrigin(1, 0)
      .setDepth(3)
      .setVisible(false);
  }

  private drawFpsIfRequested(): void {
    if (typeof location === 'undefined') return;
    const params = new URLSearchParams(location.search);
    if (!params.has('fps')) return;
    this.fpsText = this.add
      .text(GAME_WIDTH - 8, GAME_HEIGHT - 14, '60 fps', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffffff',
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      })
      .setOrigin(1, 0.5)
      .setDepth(80);
  }

  private drawSoundToggle(): void {
    const x = 12;
    const y = GAME_HEIGHT - 28;
    const bg = this.add.graphics().setDepth(2);
    const refresh = () => {
      bg.clear();
      bg.fillStyle(0x000000, 0.4);
      bg.fillRoundedRect(x, y, 60, 22, 11);
    };
    refresh();
    this.soundBtnText = this.add
      .text(x + 30, y + 11, this.soundEnabled ? '🔊 ON' : '🔇 OFF', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(3);
    const hit = this.add
      .zone(x + 30, y + 11, 60, 22)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(3);
    hit.on('pointerup', () => {
      this.soundEnabled = !this.soundEnabled;
      this.soundBtnText?.setText(this.soundEnabled ? '🔊 ON' : '🔇 OFF');
      // Sound implementation deferred to phase 2 (brief Section 1: Out of scope).
    });
  }

  private bindGameEvents(): void {
    const events = this.gameScene?.events;
    if (!events) return;
    events.on(GAME_EVENTS.ScoreChanged, this.onScoreChanged, this);
    events.on(GAME_EVENTS.ProgressChanged, this.onProgressChanged, this);
    events.on(GAME_EVENTS.StageChanged, this.onStageChanged, this);
  }

  private unbindGameEvents(): void {
    const events = this.gameScene?.events;
    if (!events) return;
    events.off(GAME_EVENTS.ScoreChanged, this.onScoreChanged, this);
    events.off(GAME_EVENTS.ProgressChanged, this.onProgressChanged, this);
    events.off(GAME_EVENTS.StageChanged, this.onStageChanged, this);
  }

  private onScoreChanged(total: number): void {
    if (!this.scoreText) return;
    // Count-up tween từ currentScore tới total (smooth)
    const obj = { value: this.currentScore };
    this.tweens.add({
      targets: obj,
      value: total,
      duration: 300,
      ease: 'Cubic.easeOut',
      onUpdate: () => this.scoreText?.setText(String(Math.round(obj.value))),
      onComplete: () => {
        this.currentScore = total;
        this.scoreText?.setText(String(total));
      },
    });
  }

  private onProgressChanged(progress: number): void {
    this.renderProgress(progress);
  }

  private onStageChanged(payload: StageChangedPayload): void {
    this.applyHearts(payload.stage);
    // Pop hearts trong khoảng prev → new (chỉ pop khi level up, không khi hit)
    if (payload.reason === 'levelup') {
      const from = Math.max(0, payload.previousStage + 1);
      for (let i = from; i <= payload.stage; i++) {
        const heart = this.hearts[i];
        if (!heart) continue;
        this.tweens.add({
          targets: heart,
          scale: { from: 1.6, to: 1 },
          duration: 280,
          ease: 'Back.easeOut',
        });
      }
    }
  }
}
